import { logger } from 'src/logger';
import { getCurrentTime, getTimeDifference } from 'src/utilities/DateTimeUtilities';
import { FilterDateFormat_MMM_YYYY } from '../FinTechOpsConstants';
import { UserMetricsParsedData } from '../user-metrics/UserMetricsInterfaces';
import {
  USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST,
  USER_METRICS_AGG_APPLICATION_BY_USERS_MEASURE_COLUMN_LIST
} from '../user-metrics/UserMetricsConstants';
import { aggregateMetricsData } from 'src/utilities/CommonUtilities';
import { BusinessReportingFilters } from './BusinessReportingSelectionFilters';
import {
  getTotalViewsByApplicationForBusinessReporting,
  getTop5ApplicationsByMonthForBusinessReporting,
  getTotalUniqueUsersByApplicationForBusinessReporting,
  convertDataToChartFormat,
  calculateChartDomains,
  createBusinessReportingMasterFilterData,
  FilterData
} from './BusinessReportingUtils';
import { MixedLineBarChartProps } from '@amzn/awsui-components-core';

// Interface for aggregated metrics data returned by aggregateMetricsData utility
interface AggregatedMetricsData {
  [key: string]: string | number;
}

// Interface for the complete processed user data structure
export interface ProcessedBusinessReportingUserData {
  aggregatedData: AggregatedMetricsData[];
  viewershipByAppData: (
    | MixedLineBarChartProps.BarDataSeries<string | number | Date>
    | MixedLineBarChartProps.LineDataSeries<string | number | Date>
  )[];
  viewershipByAppXDomain: string[];
  viewershipByAppYDomain: number[];
  top5AppsViewership: (
    | MixedLineBarChartProps.BarDataSeries<string | number | Date>
    | MixedLineBarChartProps.LineDataSeries<string | number | Date>
  )[];
  top5AppsViewershipXDomain: string[];
  top5AppsViewershipYDomain: number[];
  uniqueUsersByApplication: (
    | MixedLineBarChartProps.BarDataSeries<string | number | Date>
    | MixedLineBarChartProps.LineDataSeries<string | number | Date>
  )[];
  uniqueUsersByApplicationXDomain: string[];
  uniqueUsersByApplicationYDomain: number[];
  processedFilters: string;
}

/**
 * Processes chart data for all three user metrics charts
 * Generates chart data and domains for business reporting user section
 * @param filteredUserMetricsData - Filtered user metrics data
 * @returns Chart data for all user metrics charts
 */
export const processUserMetricsChartData = (filteredUserMetricsData: UserMetricsParsedData[], isSingleMonth = false) => {
  try {
    const chartType = isSingleMonth ? 'bar' : 'line';

    // Generate chart data using business reporting functions
    const totalViewsByAppData = getTotalViewsByApplicationForBusinessReporting(filteredUserMetricsData);
    const top5AppViewershipData = getTop5ApplicationsByMonthForBusinessReporting(filteredUserMetricsData);
    const uniqueUsersByAppData = getTotalUniqueUsersByApplicationForBusinessReporting(filteredUserMetricsData);

    // Convert data to chart format and calculate domains
    // Chart 1: Total Views by Application
    const viewsByAppChartData = convertDataToChartFormat(totalViewsByAppData, chartType);
    const viewsByAppDomains = calculateChartDomains(totalViewsByAppData);

    // Chart 2: Top 5 Sub Application - Unique Users
    const top5AppsChartData = convertDataToChartFormat(top5AppViewershipData, chartType);
    const top5AppsDomains = calculateChartDomains(top5AppViewershipData);

    // Chart 3: Total Unique Users by Application
    const uniqueUsersChartData = convertDataToChartFormat(uniqueUsersByAppData, chartType);
    const uniqueUsersDomains = calculateChartDomains(uniqueUsersByAppData);

    return {
      viewershipByAppData: viewsByAppChartData,
      viewershipByAppXDomain: viewsByAppDomains.xDomain,
      viewershipByAppYDomain: viewsByAppDomains.yDomain,
      top5AppsViewership: top5AppsChartData,
      top5AppsViewershipXDomain: top5AppsDomains.xDomain,
      top5AppsViewershipYDomain: top5AppsDomains.yDomain,
      uniqueUsersByApplication: uniqueUsersChartData,
      uniqueUsersByApplicationXDomain: uniqueUsersDomains.xDomain,
      uniqueUsersByApplicationYDomain: uniqueUsersDomains.yDomain
    };
  } catch (error) {
    logger.error('Error processing user metrics chart data', error as Error);
    return {
      viewershipByAppData: [],
      viewershipByAppXDomain: [],
      viewershipByAppYDomain: [],
      top5AppsViewership: [],
      top5AppsViewershipXDomain: [],
      top5AppsViewershipYDomain: [],
      uniqueUsersByApplication: [],
      uniqueUsersByApplicationXDomain: [],
      uniqueUsersByApplicationYDomain: []
    };
  }
};

/**
 * Main function to process all business reporting user metrics data
 * Now works with pre-filtered data (filtering centralized in BusinessReportingSelectionFilters.tsx)
 * @param filteredUserMetricsData - Pre-filtered user metrics data
 * @param filters - Business reporting filters
 * @returns Complete processed data for business reporting user section
 */
export const processBusinessReportingUserData = (
  filteredUserMetricsData: UserMetricsParsedData[],
  filters: BusinessReportingFilters
): ProcessedBusinessReportingUserData => {
  try {
    const start = getCurrentTime();

    if (filteredUserMetricsData.length === 0) {
      logger.warn('No filtered user metrics data available for business reporting');
      return getEmptyProcessedUserData(filters);
    }

    const aggregatedUserData = aggregateMetricsData(
      USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST,
      filteredUserMetricsData,
      USER_METRICS_AGG_APPLICATION_BY_USERS_MEASURE_COLUMN_LIST
    );
    const isSingleMonth = filters.period.length === 1;
    const chartData = processUserMetricsChartData(filteredUserMetricsData, isSingleMonth);
    const processedFilters = JSON.stringify({
      applications: filters.applications,
      rollupPeriod: filters.rollupPeriod,
      period: filters.period
    });

    const elapsed = getTimeDifference(start);
    logger.info(`Processed business reporting user metrics data in ${elapsed} ms`, {
      inputRecords: filteredUserMetricsData.length,
      aggregatedRecords: aggregatedUserData?.length || 0
    });

    if (!aggregatedUserData) {
      logger.warn('aggregateMetricsData returned null - no data could be aggregated', {
        inputRecords: filteredUserMetricsData.length
      });
    }

    return {
      aggregatedData: aggregatedUserData || [],
      ...chartData,
      processedFilters
    };
  } catch (error) {
    logger.error('Unable to process business reporting user metrics data', error as Error);
    return getEmptyProcessedUserData(filters);
  }
};

/**
 * Returns empty processed user data structure for error/empty states
 * Same pattern as getEmptyProcessedData in ticket processing
 * @param filters - Business reporting filters for the processed filters string
 * @returns Empty processed user data structure
 */
const getEmptyProcessedUserData = (filters: BusinessReportingFilters): ProcessedBusinessReportingUserData => {
  const processedFilters = JSON.stringify({
    applications: filters.applications,
    rollupPeriod: filters.rollupPeriod,
    period: filters.period
  });

  return {
    aggregatedData: [],
    viewershipByAppData: [],
    viewershipByAppXDomain: [],
    viewershipByAppYDomain: [],
    top5AppsViewership: [],
    top5AppsViewershipXDomain: [],
    top5AppsViewershipYDomain: [],
    uniqueUsersByApplication: [],
    uniqueUsersByApplicationXDomain: [],
    uniqueUsersByApplicationYDomain: [],
    processedFilters
  };
};

/**
 * Creates filters for business reporting user metrics
 * Follows the same pattern as createBusinessReportingMasterFilterData
 * @param userMetricsData - Processed user metrics data
 * @returns Business reporting user metrics filter options
 */
export const createUserMetricsMasterFilterData = (userMetricsData: UserMetricsParsedData[]): FilterData => {
  return createBusinessReportingMasterFilterData(userMetricsData, {
    getApplicationField: (item) => item.application_type,
    getDateField: (item) => item.log_month,
    dateFormat: FilterDateFormat_MMM_YYYY,
    logContext: 'user metrics'
  });
};
